import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import socketIOClient from 'socket.io-client';
import { useDispatch, useSelector } from "react-redux";
import { selectOld, setNotificationCount, setRoomId, notifyNewClient, notifyTransfer, notifyNewMessage, notifyFileReceived, notifyChatClosedByClient } from "../../redux/notification/notification.actions";
import { NOTIFICATION_TYPES } from "../../redux/notification/notification.types";
import NotificationSound from '../../assets/notification_1.mp3';
import PopSound from '../../assets/sounds/pop.wav';
import store from '../../redux/index.js';
import { setUserStatus } from '../../redux/user/user.actions';
import logo from '../../assets/icone SF.png';
import { appendMessages, editInstance, selectInstanceWhatsapp, selectQRCodeBase64, setCurrentChatMessages, setLoadingScreen, setPageInfo, setQRCodeBase64, setWhatsAppChats, updateWhatsAppRoom } from '../../redux/whatsapp/whatsapp.actions.js';
import { getWaitingRooms, getInProgressRooms, setViewedRooms, selectViewedRooms } from '../../redux/room/room.actions.js';
import { abasSemASala } from '../../utils/fechamentoPeloCliente';
import { mensagemDeChatEncerrado, tituloDoChatEncerrado } from '../../utils/avisoDeChatEncerrado';
import { diferencaDeConexoes } from '../../utils/conexoesDeSala';
import { formatWhatsAppMessage } from '../../helpers/formatWhatsAppMessage.js';

const WebSocketContext = createContext();

function getWhatsappIncomingNotifyKind(roomsState, jid, userId) {
  if (!jid || !userId) return 'skip';
  const waiting = roomsState.waitingRooms || [];
  const inProg = roomsState.inProgressRooms || [];

  if (waiting.some((r) => r.room_id === jid)) {
    return 'waiting';
  }

  const isMineInProgress = inProg.some(
    (r) =>
      r.room_id === jid &&
      r.user_id != null &&
      Number(r.user_id) === Number(userId)
  );
  if (isMineInProgress) {
    return 'active';
  }

  return 'waiting';
}

function normalizeCurrentRoom(raw) {
  if (!raw || Array.isArray(raw)) return null;
  return raw;
}

const NEW_CHAT_MESSAGE_EVENT = "NEW_CHAT_MESSAGE_EVENT";
const USER_STATUS_UPDATED = "USER_STATUS_UPDATED";
const NEW_CLIENT_CONNECTED = "NEW_CLIENT_CONNECTED";
const ROOM_STATUS_CHANGED = "ROOM_STATUS_CHANGED";
const ROOM_CLOSED_BY_CLIENT = "ROOM_CLOSED_BY_CLIENT";
const MESSAGE_READ_EVENT = "MESSAGE_READ_EVENT";
const SOCKET_SERVER_URL = process.env.REACT_APP_WS_URL;
const userStatus = store.getState().user.currentUser.status;

// Configurações de reconexão
const RECONNECTION_CONFIG = {
  autoConnect: true,
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 20000,
  forceNew: false,
  // Otimizações de performance
  transports: ['websocket', 'polling'],
  upgrade: true,
  rememberUpgrade: true,
  // Compressão
  perMessageDeflate: {
    threshold: 32768,
    zlibInflateOptions: {
      chunkSize: 10 * 1024
    }
  }
};

export const useWebSocket = () => {
  return useContext(WebSocketContext);
};

export const WebSocketProvider = ({ children }) => {
  /**
   * As conexões por sala vivem num REF, não em estado.
   *
   * Dois motivos, e os dois eram defeito:
   *
   * 1. A guarda "já existe conexão desta sala?" lia o estado capturado no
   *    fechamento da função. Como `addSocketConnection` era redefinida a cada
   *    render e o `setSocketConnections` só landa no render seguinte, havia uma
   *    janela em que a mesma sala abria conexão DUPLICADA.
   * 2. Ninguém fora do provider lia `socketConnections` -- conferido por grep.
   *    Guardá-lo em estado fazia cada conexão nova renderizar todo consumidor
   *    do contexto, de graça.
   *
   * Um ref é sempre atual na mesma passada e não provoca render.
   */
  const conexoesDeSalaRef = useRef({});
  const [newMessageChats, setNewMessageChats] = useState(new Set());
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [reconnectionAttempts, setReconnectionAttempts] = useState(0);

  const socketRef = useRef();
  const reconnectTimeoutRef = useRef();
  const messageQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);

  const old = useSelector(selectOld);
  const dispatch = useDispatch();
  const popNotificationaudio = useRef(new Audio(PopSound));
  const notificationAudio = useRef(new Audio(NotificationSound));
  const [hasInteracted, setHasInteracted] = useState(false);
  const qrCodeBase64 = useSelector(selectQRCodeBase64);
  const instanceWhatsapp = useSelector(selectInstanceWhatsapp);
  const instanceWhatsappRef = useRef(instanceWhatsapp);
  const lastWhatsAppWaitingNotifyRef = useRef({ jid: null, t: 0 });

  // Debounce para evitar múltiplas reconexões
  const debounceReconnect = useCallback((func, delay) => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    reconnectTimeoutRef.current = setTimeout(func, delay);
  }, []);

  // Função para processar fila de mensagens
  const processMessageQueue = useCallback(async () => {
    if (isProcessingQueueRef.current || messageQueueRef.current.length === 0) return;

    isProcessingQueueRef.current = true;

    try {
      const batch = messageQueueRef.current.splice(0, 10); // Processa em lotes de 10

      for (const message of batch) {
        try {
          await processMessage(message);
        } catch (error) {
          console.error('Erro ao processar mensagem da fila:', error);
        }
      }
    } finally {
      isProcessingQueueRef.current = false;

      // Processa próximo lote se houver mais mensagens
      if (messageQueueRef.current.length > 0) {
        setTimeout(processMessageQueue, 100);
      }
    }
  }, []);

  // Função para processar mensagem individual
  const processMessage = useCallback(async (message) => {
    // Implementação específica para cada tipo de mensagem
    if (message.type === 'whatsapp') {
      // Processa mensagem do WhatsApp
      return;
    }

    // Processa mensagem normal
    return;
  }, []);

  // Função para adicionar mensagem à fila
  const queueMessage = useCallback((message) => {
    messageQueueRef.current.push(message);

    if (!isProcessingQueueRef.current) {
      processMessageQueue();
    }
  }, [processMessageQueue]);

  // Função para reconectar automaticamente
  const reconnect = useCallback(() => {
    if (reconnectionAttempts >= RECONNECTION_CONFIG.reconnectionAttempts) {
      console.log('Máximo de tentativas de reconexão atingido');
      setConnectionStatus('failed');
      return;
    }

    setConnectionStatus('reconnecting');
    setReconnectionAttempts(prev => prev + 1);

    debounceReconnect(() => {
      if (socketRef.current) {
        socketRef.current.connect();
      }
    }, RECONNECTION_CONFIG.reconnectionDelay);
  }, [reconnectionAttempts, debounceReconnect]);

  // Função para limpar recursos
  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    // Limpa fila de mensagens
    messageQueueRef.current = [];
    isProcessingQueueRef.current = false;
  }, []);

  // Em showNotification, verifique hasInteracted antes de tocar o som
  const showNotification = (title, message, soundFile) => {
    if (!("Notification" in window)) {
      console.log("Este navegador não suporta notificações de desktop.");
    } else if (Notification.permission === "granted") {
      new Notification(title, { body: message, icon: logo });
      if (hasInteracted) {
        if (soundFile && typeof soundFile.play === 'function') {
          soundFile.play().catch(error => console.log("Playback failed:", error));
        } else {
          console.warn('Som de notificação inválido:', soundFile);
        }
      }
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then(permission => {
        if (permission === "granted") {
          new Notification(title, { body: message, icon: logo });
          if (hasInteracted) {
            if (soundFile && typeof soundFile.play === 'function') {
              soundFile.play().catch(error => console.log("Playback failed:", error));
            } else {
              console.warn('Som de notificação inválido:', soundFile);
            }
          }
        }
      });
    }
  };

  useEffect(() => {
    instanceWhatsappRef.current = instanceWhatsapp;
  }, [instanceWhatsapp]);

  useEffect(() => {
    if (hasInteracted) {
      popNotificationaudio.current.load(); // Pré-carrega o som
      notificationAudio.current.load(); // Pré-carrega o som
    }
  }, [hasInteracted]);

  // Configuração da Conexão Principal ao Carregar o Componente
  useEffect(() => {
    // Cria conexão com configurações otimizadas
    socketRef.current = socketIOClient(SOCKET_SERVER_URL, RECONNECTION_CONFIG);

    // Eventos de conexão
    socketRef.current.on('connect', () => {
      console.log('WebSocket conectado');
      setConnectionStatus('connected');
      setReconnectionAttempts(0);

      // Processa fila de mensagens pendentes
      if (messageQueueRef.current.length > 0) {
        processMessageQueue();
      }
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('WebSocket desconectado:', reason);
      setConnectionStatus('disconnected');

      if (reason === 'io server disconnect') {
        // Reconecta se desconectado pelo servidor
        reconnect();
      }
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('Erro de conexão:', error);
      setConnectionStatus('error');

      // Tenta reconectar em caso de erro
      if (reconnectionAttempts < RECONNECTION_CONFIG.reconnectionAttempts) {
        reconnect();
      }
    });

    socketRef.current.on('reconnect', (attemptNumber) => {
      console.log('Reconectado após', attemptNumber, 'tentativas');
      setConnectionStatus('connected');
      setReconnectionAttempts(0);
    });

    socketRef.current.on('reconnect_attempt', (attemptNumber) => {
      console.log('Tentativa de reconexão:', attemptNumber);
      setConnectionStatus('reconnecting');
    });

    socketRef.current.on('reconnect_failed', () => {
      console.log('Falha na reconexão');
      setConnectionStatus('failed');
    });

    // Eventos globais
    socketRef.current.on(USER_STATUS_UPDATED, (data) => {
      const { userId, status } = data;
      dispatch(setUserStatus({ userId, status }));
    });

    socketRef.current.on(NEW_CLIENT_CONNECTED, (payload = {}) => {
      const { room_id: waitingRoomJid } = payload;

      if (waitingRoomJid) {
        lastWhatsAppWaitingNotifyRef.current = {
          jid: waitingRoomJid,
          t: Date.now(),
        };
      }

      // Fase 4: a fila agora vive no Painel de Atendimentos e a página "Em Espera" foi
      // removida. Não notificamos mais "novo cliente em espera"; apenas atualizamos os
      // dados. O operador que RECEBE o atendimento é avisado pelo piscar da aba
      // (NewAssignedChatWatcher).
      dispatch(getWaitingRooms());
      // O chat pode ter sido distribuído automaticamente para ESTE operador (item 8).
      // Atualiza a lista de "em progresso" para o watcher abrir/piscar a aba do novo
      // atendimento. Reforço em 1.5s cobre eventual corrida na atribuição.
      const meOnNewClient = store.getState().user.currentUser;
      if (meOnNewClient?.id) {
        dispatch(getInProgressRooms(meOnNewClient.id));
        setTimeout(() => dispatch(getInProgressRooms(meOnNewClient.id)), 1500);
      }
    });

    socketRef.current.on(ROOM_STATUS_CHANGED, (data) => {
      dispatch(notifyTransfer(data.name));
      showNotification("Novo cliente em espera", `Conversa transferida de ${data.name}`, popNotificationaudio.current);
      dispatch(getWaitingRooms());
      // Transferência pode ter caído neste operador: atualiza o in-progress p/ a aba piscar.
      const meOnStatusChange = store.getState().user.currentUser;
      if (meOnStatusChange?.id) {
        dispatch(getInProgressRooms(meOnStatusChange.id));
        setTimeout(() => dispatch(getInProgressRooms(meOnStatusChange.id)), 1500);
      }
    });

    // Evento global: o cliente encerrou um chat. Só interessa ao operador responsável
    // por aquela sala — os demais simplesmente ignoram.
    socketRef.current.on(ROOM_CLOSED_BY_CLIENT, (data) => {
      const currentUser = store.getState().user.currentUser;
      if (!currentUser || !data?.operatorUserId || String(data.operatorUserId) !== String(currentUser.id)) {
        return;
      }

      /*
       * O MESMO texto na notificação do navegador e no aviso gravado -- é o
       * que `utils/avisoDeChatEncerrado` existe para garantir. Antes, aqui
       * dizia "encerrou a conversa" e o sininho dizia "encerrou o chat. Um
       * ticket foi gerado", sem o número do chamado em nenhum dos dois.
       */
      /*
       * O MOTIVO escolhe o título e a frase: o mesmo evento carrega o
       * encerramento PELO CLIENTE e o POR INATIVIDADE, e os dois levam o
       * operador a coisas diferentes -- o primeiro costuma significar assunto
       * resolvido, o segundo costuma pedir retorno.
       *
       * `data.motivo` pode não vir (api anterior a 03/11), e aí as duas
       * funções caem no texto do cliente, que é o caso que já existia.
       */
      const aviso = mensagemDeChatEncerrado({
        clientName: data.clientName,
        clientCode: data.clientCode,
        ticketNumber: data.ticketNumber,
        motivo: data.motivo,
      });

      showNotification(tituloDoChatEncerrado(data.motivo), aviso, popNotificationaudio.current);

      if (data.ticketId) {
        /*
         * Fica registrada no sininho em vez de só um toast, assim o operador
         * não perde o aviso se não vir na hora. O toast preso no canto (com o
         * botão que abre o chamado) é levantado pelo `NotificationBadge` a
         * partir desta notificação.
         *
         * `ticketNumber` é o número que a TELA mostra, e é dele que sai o
         * código no aviso e no botão. Ele pode faltar (acervo importado), e
         * nesse caso o aviso sai sem código -- nunca "#undefined".
         */
        dispatch(notifyChatClosedByClient(
          data.ticketId,
          data.clientName,
          currentUser.id,
          data.ticketNumber,
          data.clientCode,
          data.motivo
        ));
      }

      /*
       * A conversa SAI do acesso rápido (ponto 6 da Gisele, 15/10/2026).
       *
       * Era a metade que faltava: o aviso já existia, mas a aba continuava lá
       * apontando para uma conversa que acabou -- o operador clicava e caía
       * num chat morto. Quando quem encerra é o OPERADOR, `ChatOptions` já
       * tira a aba; aqui ninguém tirava, porque o encerramento vem de fora da
       * tela dele.
       *
       * A regra mora em `utils/fechamentoPeloCliente`, com teste -- aqui
       * dentro ela seria intestável sem simular socket e store.
       *
       * O ticket NÃO é aberto daqui: o aviso com o botão "Ir para o ticket"
       * é levantado pelo `NotificationBadge`, a partir da notificação
       * despachada logo acima.
       */
      const estado = store.getState();

      dispatch(setViewedRooms(abasSemASala(selectViewedRooms(estado), data.roomId)));
    });

    socketRef.current.on('qr', (...args) => {
      const qr = args[0];
      const key = args[1];
      console.log('QR RECEIVED', qr?.slice?.(0, 40));
      console.log('KEY', key);
      if (qr == null) return;
      const instance = instanceWhatsappRef.current.find((inst) => inst.key === key);
      if (instance && instance.phone_connected) {
        dispatch(editInstance({ key, phone_connected: false }));
      }
      dispatch(setQRCodeBase64(qr));
    });

    socketRef.current.on('loading_screen', ({ percent, message, key }) => {
      console.log('LOADING SCREEN', percent, message, key);
      const p = Number(percent);
      if (!Number.isNaN(p) && p >= 100) {
        dispatch(setQRCodeBase64('ready'));
      }
      dispatch(setLoadingScreen(Number.isNaN(p) ? percent : p, message, key));
    });

    socketRef.current.on('whatsapp_ready', (payload) => {
      const data = payload && typeof payload === 'object' ? payload : {};
      const { phone_connected, key, chats, fromSocket = false } = data;
      console.log('whatsapp_ready', phone_connected, key, chats?.length, fromSocket);
      const isConnected =
        phone_connected === true || phone_connected === 1 || phone_connected === '1';
      if (isConnected) {
        dispatch(setQRCodeBase64('ready'));
      }
      if (key != null && String(key).trim() !== '') {
        const keyStr = String(key).replace(/\D/g, '') || String(key);
        dispatch(editInstance({ key: keyStr, phone_connected: !!isConnected }));
      }
      let nextChats = chats;
      if (nextChats !== undefined && nextChats !== null) {
        localStorage.setItem('whatsapp_chat', JSON.stringify(nextChats));
      } else {
        try {
          nextChats = JSON.parse(localStorage.getItem('whatsapp_chat') || '[]');
        } catch {
          nextChats = [];
        }
      }
      dispatch(setWhatsAppChats(Array.isArray(nextChats) ? nextChats : []));
      console.log('Client is ready!');
    });

    socketRef.current.on('whatsapp_message', ({ message, whatsAppChats }) => {
      if (message.fromMe) return;
      localStorage.setItem('whatsapp_chat', JSON.stringify(whatsAppChats));
      console.log(message)
      dispatch(setWhatsAppChats(whatsAppChats));
      // dispatch(addNotification(
      //   NOTIFICATION_TYPES.NEW_MESSAGE,
      //   'Nova Mensagem',
      //   message.body,
      //   message.roomId
      // ));
      console.log('MESSAGE');
    });

    socketRef.current.on('whatsapp_message_from_me', ({ message, whatsAppChats }) => {
      if (!message.fromMe) return;

      localStorage.setItem('whatsapp_chat', JSON.stringify(whatsAppChats));
      dispatch(setWhatsAppChats(whatsAppChats));
      const instance = instanceWhatsappRef.current
        .filter(instance => instance.key === message.from.split('@')[0]);
      dispatch(updateWhatsAppRoom({
        whatsapp_instance_id: instance[0].id,
        whatsapp_remote_id: message.from.split('@')[0],
        message_body: message.body,
        timestamp: message.timestamp,
      }));
      console.log('ME');
    });

    socketRef.current.on('whatsapp_open_chat', ({ chatInfo, chatMessages, pageInfo }) => {
      const currentUser = store.getState().user.currentUser;

      if (pageInfo.page !== pageInfo.total_pages) {
        console.log(pageInfo);
      }
      const formattedMessages = chatMessages
        .map((msg) => {
          if (msg?.message) return msg;
          if (msg?.message === '') return null;

          const baseTimestamp = msg.timestamp || msg.t;
          msg.timestamp = baseTimestamp;

          return formatWhatsAppMessage(msg, currentUser);
        })
        .filter(Boolean)
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      dispatch(setPageInfo(pageInfo));
      dispatch(setCurrentChatMessages(formattedMessages));
    });

    socketRef.current.on('update_chat_messages', async ({ whatsAppChats, chatMessages }) => {
      console.log('🔍 update_chat_messages RECEBIDO:', { whatsAppChats, chatMessages });

      const currentUser = store.getState().user.currentUser;
      const currentRoom = normalizeCurrentRoom(store.getState().rooms.currentRoom);

      console.log(chatMessages);
      const messages = Array.isArray(chatMessages) ? chatMessages : [chatMessages];

      const roomJid = (msg) =>
        msg?.id?.remote || msg?.remote || msg?.roomId || msg?.room_id;

      const filteredNewMessages = messages
        .filter((msg) => roomJid(msg) === currentRoom?.room_id)
        .map((msg) => {
          if (msg.message) return msg;

          if (msg.timestamp || msg.t) {
            const baseTimestamp = msg.timestamp || msg.t;
            msg.timestamp = baseTimestamp;
          }

          return formatWhatsAppMessage(msg, currentUser);
        })
        .filter(Boolean);

      const head = messages[0];
      const targetJid = head ? roomJid(head) : null;

      if (
        head &&
        head.fromMe === false &&
        currentUser &&
        currentUser.id &&
        targetJid
      ) {
        const senderName = head.notifyName || head.sender_name || 'Cliente';
        const messagePreview = head.body || head.message || 'Nova mensagem';
        const isFile =
          head.hasMedia ||
          head.type === 'image' ||
          head.type === 'document' ||
          head.type === 'audio' ||
          head.type === 'video';

        await Promise.all([
          dispatch(getWaitingRooms()),
          dispatch(getInProgressRooms(currentUser.id)),
        ]);

        const roomsState = store.getState().rooms;
        const kind = getWhatsappIncomingNotifyKind(
          roomsState,
          targetJid,
          currentUser.id
        );

        if (kind === 'active') {
          if (isFile) {
            dispatch(
              notifyFileReceived(
                targetJid,
                head.body || 'Arquivo recebido',
                senderName
              )
            );
          } else {
            dispatch(
              notifyNewMessage(
                targetJid,
                messagePreview,
                senderName,
                head.id?.id || head.message_id
              )
            );
          }
        }
        // Fase 4: mensagens de WhatsApp para salas ainda na fila não geram mais a
        // notificação "novo cliente em espera" — a fila é acompanhada pelo Painel de
        // Atendimentos.
      }

      if (filteredNewMessages.length > 0) {
        dispatch(appendMessages(filteredNewMessages));
      }
      dispatch(setWhatsAppChats(whatsAppChats));
    });

    socketRef.current.on('message_ack', ({ message, whatsAppChats, chatMessages }) => {
      localStorage.setItem('whatsapp_chat', JSON.stringify(whatsAppChats));
      dispatch(setWhatsAppChats(whatsAppChats));
      dispatch(setCurrentChatMessages(chatMessages));
    });

    socketRef.current.on('whatsapp_disconnected', ({ reason, key }) => {
      console.log('whatsapp_disconnected', reason, key);
      dispatch(setQRCodeBase64(null));
      dispatch(setLoadingScreen(0, '', ''));
      const keyStr = key != null ? String(key).replace(/\D/g, '') : '';
      if (keyStr) {
        dispatch(editInstance({ key: keyStr, phone_connected: false }));
      } else {
      }
      console.log('Client is disconnected!');
    });

    socketRef.current.on('auth_failure', ({ message }) => {
      console.log('auth_failure', message);
      // Limpa o QR code quando há falha na autenticação
      dispatch(setQRCodeBase64(null));
      // Pode adicionar uma notificação para o usuário aqui se necessário
      alert('Falha na autenticação. Tente escanear o QR code novamente.');
    });

    // Novos eventos para tratamento de erros
    socketRef.current.on('rate_limit_exceeded', ({ message }) => {
      console.warn('Rate limit excedido:', message);
      // Pode mostrar uma notificação para o usuário
    });

    socketRef.current.on('message_error', ({ error, details }) => {
      console.error('Erro na mensagem:', error, details);
      // Pode mostrar uma notificação para o usuário
    });

    return cleanup;
  }, [dispatch, reconnect, processMessageQueue, cleanup]);

  useEffect(() => {
    const handleUserInteraction = () => {
      setHasInteracted(true);
      window.removeEventListener('click', handleUserInteraction);
    };

    window.addEventListener('click', handleUserInteraction);

    return () => {
      window.removeEventListener('click', handleUserInteraction);
    };
  }, []);

  const addSocketConnection = (roomId, roomData) => {
    if (!conexoesDeSalaRef.current[String(roomId)]) {
      const roomSocket = socketIOClient(SOCKET_SERVER_URL, {
        query: { roomId },
        ...RECONNECTION_CONFIG
      });

      // Os `console.log` de conectar/desconectar saíram: com uma conexão por
      // sala, eles enchiam o console do operador a cada carga da lista e
      // escondiam o que importa.
      roomSocket.on('connect_error', (error) => {
        console.error(`Error connecting to room ${roomId}:`, error);
      });

      roomSocket.on(NEW_CHAT_MESSAGE_EVENT, (messages) => {
        if (!Array.isArray(messages) || messages.length === 0) return;

        const lastMessage = messages[messages.length - 1];

        // Só criar notificação se for mensagem do cliente (type === 0)
        if (lastMessage && lastMessage.type === 0) {
          // Verificar se é um arquivo
          const isFile = lastMessage.archive || lastMessage.type === 'file';
          const notificationTitle = isFile ? 'Novo arquivo' : 'Nova mensagem';
          const notificationMessage = isFile ? 'Arquivo recebido' : (lastMessage.message || 'Nova mensagem recebida');

          // Adicionar notificação ao sistema
          if (isFile) {
            dispatch(notifyFileReceived(
              roomId,
              lastMessage.archive?.name || 'Arquivo',
              lastMessage.sender_name
            ));
          } else {
            dispatch(notifyNewMessage(
              roomId,
              lastMessage.message || 'Nova mensagem recebida',
              lastMessage.sender_name,
              lastMessage.id
            ));
          }

          // Tocar som e mostrar notificação do navegador
          showNotification("Cliente", notificationMessage, notificationAudio.current);

          // Atualizar contador de mensagens não lidas
          dispatch(setRoomId(roomId));
          setNewMessageChats((prevChats) => new Set(prevChats).add(roomId));
        }

        console.log(messages)
      });

      roomSocket.on(MESSAGE_READ_EVENT, () => {
        setNewMessageChats((prevChats) => {
          const updatedChats = new Set(prevChats);  // Cria uma cópia do Set existente
          updatedChats.delete(roomId);              // Remove o roomId
          return updatedChats;                      // Retorna o novo Set atualizado
        });
      });

      conexoesDeSalaRef.current[String(roomId)] = roomSocket;
    }
  };

  /**
   * Fecha a conexão de uma sala, se houver.
   *
   * Antes isto não existia de verdade: `addSocketConnection` terminava com
   * `return () => roomSocket.disconnect()`, mas ela é uma função comum e não um
   * `useEffect` -- a limpeza era descartada e **nenhuma conexão fechava nunca**.
   */
  const removeSocketConnection = (roomId) => {
    const chave = String(roomId);
    const socket = conexoesDeSalaRef.current[chave];

    if (!socket) return;

    try {
      socket.removeAllListeners();
      socket.disconnect();
    } catch (e) {
      console.error(`Erro ao encerrar a conexão da sala ${chave}:`, e);
    }

    delete conexoesDeSalaRef.current[chave];
  };

  /**
   * Deixa as conexões iguais à lista de salas: abre o que falta, fecha o que sobra.
   *
   * IDEMPOTENTE de propósito -- é essa propriedade que permite ao efeito que a
   * chama disparar à vontade sem criar conexão repetida. A decisão em si mora em
   * `utils/conexoesDeSala`, com teste.
   *
   * Uma conexão POR SALA continua sendo o desenho: é o que o servidor de ws
   * espera, com `roomId` na query. Consolidar numa só exigiria mexer no servidor.
   */
  const sincronizarConexoesDeSala = useCallback((salas) => {
    const lista = Array.isArray(salas) ? salas : [];
    const desejados = lista
      .filter((s) => s && s.room_id !== null && s.room_id !== undefined)
      .map((s) => String(s.room_id));

    const { abrir, fechar } = diferencaDeConexoes(
      desejados,
      Object.keys(conexoesDeSalaRef.current)
    );

    fechar.forEach((id) => removeSocketConnection(id));
    abrir.forEach((id) => {
      const sala = lista.find((s) => String(s.room_id) === id);
      addSocketConnection(id, sala);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ao desmontar o provider (logout, recarregar), encerra tudo.
  //
  // Ler `conexoesDeSalaRef.current` DENTRO da limpeza é intencional, e o aviso
  // do lint sobre isso não se aplica aqui: o que se quer fechar é exatamente o
  // que existir no momento de desmontar. Copiar o ref para uma variável na
  // montagem -- que é o que o lint sugere -- capturaria o objeto vazio do
  // início e não fecharia conexão nenhuma.
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      Object.keys(conexoesDeSalaRef.current).forEach((id) => removeSocketConnection(id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateUserStatus = (data) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit(USER_STATUS_UPDATED, { userId: data.userId, status: data.status });
    } else {
      // Adiciona à fila se não estiver conectado
      queueMessage({ type: 'status_update', data });
    }
  };

  const roomStatusChanged = (data) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit(ROOM_STATUS_CHANGED, data);
    } else {
      // Adiciona à fila se não estiver conectado
      queueMessage({ type: 'room_status', data });
    }
  };

  const eventEmit = (event, param) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit(event, param);
    } else {
      // Adiciona à fila se não estiver conectado
      queueMessage({ type: 'custom_event', event, param });
    }
  };

  const value = {
    // `socketConnections` saiu: ninguém fora do provider o lia (conferido por
    // grep) e mantê-lo em estado fazia cada conexão nova renderizar todos os
    // consumidores do contexto.
    sincronizarConexoesDeSala,
    addSocketConnection,
    newMessageChats,
    userStatus,
    updateUserStatus,
    roomStatusChanged,
    eventEmit,
    connectionStatus,
    reconnect
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
